import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Facebook, Instagram, Close } from "@mui/icons-material";

// Background image
import thirdimage from "../../assets/centrum_assets/design/index/sixthBG.png";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

import logoImage from "../../assets/centrum_assets/design/index/LOGO.png";
import axiosClient from "axiosClient";

const Socialmedia = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(null);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch Instagram posts from your backend
  useEffect(() => {
    const fetchInstagramPosts = async () => {
      try {
        setLoading(true);
        const response = await axiosClient.get('/instagram');

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to fetch Instagram posts");
        }

        // Process the posts - filter for images and carousel albums, exclude videos
        const processedPosts = response.data.data
          .filter(post =>
            post.media_type === "IMAGE" ||
            post.media_type === "CAROUSEL_ALBUM"
          )
          .map(post => ({
            id: post.id,
            image: post.media[0], // Get the first image from the media array
            description: post.caption || "No caption available",
            mediaType: post.media_type,
          }))
          .slice(0, 12); // Limit to 12 most recent posts

        setInstagramPosts(processedPosts);
        setError(null);
      } catch (err) {
        console.error("Error fetching Instagram posts:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramPosts();
  }, []);

  // Don't render component if there's an error
  if (error) {
    return null;
  }

  const handleOpen = (slide) => {
    setActiveSlide(slide);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setActiveSlide(null);
  };

  return (
    <>
      <Box
        sx={{
          backgroundImage: `url(${thirdimage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
          pb: 10,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            mt: { lg: 15, xs: 5 },
            maxWidth: { lg: "70%", md: "95%", sm: "90%", xs: "90%" },
          }}
        >
          <Grid container spacing={8} alignItems="center">
            {/* Left Side */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="h5"
                sx={{ mb: 0.5, fontWeight: "bold", color: "#00b5e2" }}
              >
                FOLLOW US ON SOCIAL MEDIA
              </Typography>
              <Typography variant="h2" fontWeight={800} sx={{ color: "#0a1a3b" }}>
                GET CONNECTED
              </Typography>
            </Grid>

            {/* Right Text */}
            <Grid item xs={12} md={8}>
              <Typography
                variant="h5"
                sx={{
                  color: "#3a3a3a",
                  lineHeight: 1.8,
                  letterSpacing: "1.8px",
                  mt: { xs: -6, md: 0 },
                }}
              >
                We're on Instagram and Facebook. Check out our feeds and see what
                we're up to. We're always looking to engage with our community, so
                follow us on social media!
              </Typography>
            </Grid>
          </Grid>

          {/* Loading State */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <CircularProgress sx={{ color: "#00b5e2" }} />
            </Box>
          )}

          {/* Instagram Posts Carousel */}
          {!loading && instagramPosts.length > 0 && (
            <Box sx={{ mt: 5 }}>
              <Swiper
                modules={[Navigation]}
                spaceBetween={20}
                slidesPerView={4}
                navigation={true}
                loop={instagramPosts.length > 4}
                style={{ width: "100%", paddingBottom: "40px" }}
                breakpoints={{
                  1200: {
                    slidesPerView: 4,
                  },
                  900: {
                    slidesPerView: 3,
                  },
                  600: {
                    slidesPerView: 2,
                  },
                  0: {
                    slidesPerView: 2,
                  },
                }}
              >
                {instagramPosts.map((post) => (
                  <SwiperSlide key={post.id}>
                    <Box
                      sx={{
                        width: "100%",
                        position: "relative",
                        paddingTop: "100%",
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        cursor: "pointer",
                        "&:hover img": {
                          transform: "scale(1.05)",
                          transition: "transform 0.3s ease",
                        },
                      }}
                      onClick={() => handleOpen(post)}
                    >
                      <img
                        src={post.image}
                        alt={`Instagram post ${post.id}`}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "10px",
                          transition: "transform 0.3s ease",
                        }}
                        loading="lazy"
                      />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
          )}

          {/* No Posts State */}
          {!loading && instagramPosts.length === 0 && (
            <Box sx={{ mt: 5, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                No Instagram posts available at the moment.
              </Typography>
            </Box>
          )}

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mt: 4,
            }}
          >
            <Button
              startIcon={<Facebook />}
              component="a"
              href="https://www.facebook.com/centrumfuel"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: "uppercase",
                color: "#00b5e2",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              LIKE US ON FACEBOOK
            </Button>

            <Button
              startIcon={<Instagram />}
              component="a"
              href="https://www.instagram.com/centrumfuel.ph/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: "uppercase",
                color: "#7BB241",
                fontWeight: 600,
                fontSize: "14px",
                "&:hover": {
                  color: "#00b5e2",
                },
              }}
            >
              FOLLOW US ON INSTAGRAM
            </Button>
          </Box>
        </Container>

        {/* Modal */}
        <Modal
          open={modalOpen}
          onClose={handleClose}
          aria-labelledby="modal-slide-title"
          aria-describedby="modal-slide-description"
          closeAfterTransition
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              maxWidth: 900,
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              p: 3,
              position: "relative",
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={handleClose}
              sx={{ position: "absolute", top: 8, right: 8 }}
              aria-label="close modal"
            >
              <Close />
            </IconButton>

            <Grid container spacing={2} sx={{ height: "100%" }}>
              {/* Left side: Large image */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  <img
                    src={activeSlide?.image}
                    alt="Instagram post"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
              </Grid>

              {/* Right side: Description on top, logo on bottom */}
              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  pl: 2,
                  borderLeft: { md: "1px solid #ddd" },
                }}
              >
                {/* Description on top right */}
                <Box>
                  <Typography
                    id="modal-slide-title"
                    variant="h5"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Instagram Post
                  </Typography>
                  <Typography
                    id="modal-slide-description"
                    variant="body1"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {activeSlide?.description || "No caption available."}
                  </Typography>
                </Box>

                {/* Logo bottom right */}
                <Box sx={{ mt: 2 }}>
                  <img
                    src={logoImage}
                    alt="Logo"
                    style={{ maxWidth: "120px", maxHeight: "60px" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default Socialmedia;